import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 py-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="nm-flat rounded-2xl p-8 md:p-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Privacy Policy
          </h1>
          <p className="text-gray-500 mb-8">Last updated: June 27, 2025</p>

          {/* Section 1 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              1. Information We Collect
            </h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span><strong>Account Information:</strong> Name, email, password.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span><strong>Usage Data:</strong> Pages visited, time spent, IP address, device/browser type.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span><strong>Cookies & Tracking:</strong> We use cookies and similar technologies to improve your experience.</span>
              </li>
            </ul>
          </section>

          {/* Section 2 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              2. How We Use Your Information
            </h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span>To provide, maintain, and improve GoodAcademy.ai.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span>To communicate with you (e.g., account notices, newsletters).</span>
              </li>
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span>To analyze usage trends and personalize content.</span>
              </li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              3. Data Sharing & Disclosure
            </h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span><strong>Service Providers:</strong> We may share data with trusted third parties who perform services on our behalf (e.g., hosting, analytics).</span>
              </li>
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span><strong>Legal Requirements:</strong> We may disclose information if required by law or to protect our rights.</span>
              </li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              4. Cookies & Analytics
            </h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span>You can control cookies through your browser settings.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span>
                  We use Google Analytics to understand Site usage; learn more at{' '}
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-800 underline"
                  >
                    https://policies.google.com/privacy
                  </a>.
                </span>
              </li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              5. Security
            </h2>
            <p className="text-gray-700">
              We implement reasonable technical and organizational measures to protect your data, but cannot guarantee absolute security.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              6. Your Choices
            </h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span><strong>Access & Correction:</strong> You may update or delete your account information at any time.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-purple-500 font-bold">•</span>
                <span><strong>Email Opt-Out:</strong> You can unsubscribe from marketing emails via the link in any message.</span>
              </li>
            </ul>
          </section>

          {/* Section 7 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              7. Children's Privacy
            </h2>
            <p className="text-gray-700">
              The Site is not directed to children under 13. We do not knowingly collect data from minors.
            </p>
          </section>

          {/* Section 8 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              8. International Transfers
            </h2>
            <p className="text-gray-700">
              If you're outside the United States, your data may be transferred to and processed in the State of New York in accordance with this Policy.
            </p>
          </section>

          {/* Section 9 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              9. Changes to This Policy
            </h2>
            <p className="text-gray-700">
              We may update this Privacy Policy by posting a new version. We'll note the date of revision at the top.
            </p>
          </section>

          {/* Section 10 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              10. Governing Law
            </h2>
            <p className="text-gray-700">
              This Privacy Policy is governed by the laws of the State of New York, without regard to its conflicts-of-law principles.
            </p>
          </section>

          {/* Section 11 */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              11. Contact Us
            </h2>
            <p className="text-gray-700 mb-2">
              For privacy questions, please contact:
            </p>
            <div className="text-gray-700">
              <p className="font-medium">Good Academy, Inc.</p>
              <a
                href="mailto:info@GoodAcademy.ai"
                className="text-purple-600 hover:text-purple-800 underline"
              >
                info@GoodAcademy.ai
              </a>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
